/**
 * Generate the URLs needed to support user background images on wish lists. Use in scoped style tag
 * @param {string} uuid  The Cloudinary public id, e.g. ugc/lists/masthead/2db34b40-8444-43f3-a983-25f99368a330
 * @param {{x: number, y: number}|null} focus  Owner-chosen focal point as
 *   percentages of the image (BH-38). Null keeps Cloudinary's automatic
 *   gravity and a centred CSS position.
 *
 * With a focal point the crop is centred on it (g_xy_center takes relative
 * coordinates on this cloud), and `position` carries the matching
 * background-position so the residual bg-cover crop for the container's
 * own aspect keeps the same spot in frame.
 */
export function useMastheadImageUrls(uuid, focus = null) {
    const hasFocus =
        focus && Number.isFinite(focus.x) && Number.isFinite(focus.y);
    const gravity = hasFocus
        ? `g_xy_center,x_${(focus.x / 100).toFixed(2)},y_${(focus.y / 100).toFixed(2)}`
        : "g_auto";
    const src = (dpr, h, w) =>
        `https://res.cloudinary.com/gift-hero/image/upload/dpr_${dpr},c_lfill,h_${h},w_${w},${gravity},q_auto:good/${uuid}.jpg`;
    const variant = (dpr, h, w) => `url(${src(dpr, h, w)})`;

    // Setup the Cloudinary transformation URLs manually. The *Src keys are
    // the bare URLs for <img> use (the expert list cover panel, GH-1299).
    return {
        small1x: variant("1.0", 240, 600),
        small2x: variant("2.0", 240, 600),
        large1x: variant("1.0", 280, 1044),
        large2x: variant("2.0", 280, 1044),
        small2xSrc: src("2.0", 240, 600),
        large2xSrc: src("2.0", 280, 1044),
        // Expert card cover (271×186 at 2x).
        card2xSrc: src("2.0", 186, 271),
        // Gift Ideas featured card cover: the full-width strip below md
        // (up to ~640×150) and the 291×288 panel from md up, at 2x.
        featureStrip2xSrc: src("2.0", 150, 640),
        featureSquare2xSrc: src("2.0", 288, 291),
        position: hasFocus ? `${focus.x}% ${focus.y}%` : "center",
    };
}
